import { useEffect, useMemo } from "react";
import { asksDesktopApproval } from "@/components/cursors/agent-cursor-intent";
import {
  canvasCursorPaintsOnDisplay,
  canvasCursorVisibility,
} from "@/components/cursors/agent-cursor-layer-visibility";
import {
  activeGuidePresentationId,
  useActiveGuidePresentationId,
} from "@/components/cursors/use-active-guide-presentation-id";
import {
  cursorRunIdentity,
  runSurfaceOwner,
  sameRun,
  type RunIdentity,
  type RunSurface,
  type VisibleConversation,
} from "@/lib/run-surface-owner";
import { useCanvasStore, type AgentCursor } from "@/stores/canvas-store";
import { useCompactAgentDisplayStore } from "@/stores/compact-agent-display-store";
import {
  useCompanionStore,
  type ActiveExecution,
  type AgentSession,
} from "@/stores/companion-store";
import { useConversationStore } from "@/stores/conversation-store";
import { useDesktopMode, type DesktopMode } from "@/stores/desktop-mode-store";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { usePresentationGuideStore } from "@/stores/presentation-guide-store";
import { useProjectStore } from "@/stores/project-store";
import { getCompactCursorState } from "./compact-agent-cursor-state";
import { resolveCursorConversationId } from "./cursor-conversation-id";
import {
  activeContextualStory,
  contextualCursorExecutionIds,
  useContextualCursorExecutions,
} from "./onboarding/use-contextual-cursor-execution";
import { useActiveDisplay } from "./use-active-display";
import { getWindowDisplayId } from "./use-window-role";
import {
  getVisibleComputerConversations,
  useVisibleComputerConversations,
} from "./visible-computer-conversations";
import {
  attachRunSurfaceRegistry,
  publishLocalRunSurfaces,
  unionRunSurfaces,
  useRunSurfaceRegistry,
  type RenderedCursorRun,
} from "./run-surface-registry";

/** What this window knows when it picks the one surface for a run. */
export interface RunSurfaceContext {
  visibleConversations: readonly VisibleConversation[];
  dockEnabled: boolean;
  mode: DesktopMode;
  agentCursors: Map<string, AgentCursor>;
  executions: Map<string, ActiveExecution>;
  agentSessions: Record<string, AgentSession>;
  projectId?: string;
  spaceId?: string;
  displayId: number | null;
  activeDisplayId: number | null;
  activeGuideId: string | null;
  contextualExecutionIds: readonly string[];
  /** Cursor surfaces reported by all webviews, independent of mirrored runtime state. */
  renderedCursors?: readonly RenderedCursorRun[];
}

export function getRunSurfaceContext(): RunSurfaceContext {
  const canvas = useCanvasStore.getState();
  const companion = useCompanionStore.getState();
  const project = useProjectStore.getState();
  const local: RunSurfaceContext = {
    visibleConversations: getVisibleComputerConversations(),
    dockEnabled: useCompactAgentDisplayStore.getState().dockEnabled,
    mode: useDesktopMode.getState().mode,
    agentCursors: canvas.agentCursors,
    executions: companion.executions,
    agentSessions: companion.agentSessions,
    projectId: project.currentProject?.id,
    spaceId: project.currentSpace?.id,
    displayId: getWindowDisplayId(),
    activeDisplayId: useActiveDisplay.getState().activeDisplayId,
    activeGuideId: activeGuidePresentationId(
      usePresentationGuideStore.getState(),
    ),
    contextualExecutionIds: contextualCursorExecutionIds(
      activeContextualStory(useOnboardingStore.getState()),
      useConversationStore.getState().messages,
      companion.executions,
      canvas.awaitingInputs,
    ),
  };
  local.visibleConversations = unionRunSurfaces(
    local.visibleConversations,
    [],
  ).conversations;
  return {
    ...local,
    renderedCursors: unionRunSurfaces([], localRenderedCursorRuns(local))
      .cursors,
  };
}

export function useRunSurfaceContext(): RunSurfaceContext {
  const localConversations = useVisibleComputerConversations();
  const reports = useRunSurfaceRegistry((state) => state.reports);
  const visibleConversations = useMemo(
    () => unionRunSurfaces(localConversations, [], reports).conversations,
    [localConversations, reports],
  );
  const dockEnabled = useCompactAgentDisplayStore((state) => state.dockEnabled);
  const mode = useDesktopMode((state) => state.mode);
  const agentCursors = useCanvasStore((state) => state.agentCursors);
  const executions = useCompanionStore((state) => state.executions);
  const agentSessions = useCompanionStore((state) => state.agentSessions);
  const projectId = useProjectStore((state) => state.currentProject?.id);
  const spaceId = useProjectStore((state) => state.currentSpace?.id);
  const activeDisplayId = useActiveDisplay((state) => state.activeDisplayId);
  const activeGuideId = useActiveGuidePresentationId();
  const contextualExecutionIds = useContextualCursorExecutions();
  const displayId = getWindowDisplayId();
  const local = useMemo(
    () => ({
      visibleConversations,
      dockEnabled,
      mode,
      agentCursors,
      executions,
      agentSessions,
      projectId,
      spaceId,
      displayId,
      activeDisplayId,
      activeGuideId,
      contextualExecutionIds,
    }),
    [
      visibleConversations,
      dockEnabled,
      mode,
      agentCursors,
      executions,
      agentSessions,
      projectId,
      spaceId,
      displayId,
      activeDisplayId,
      activeGuideId,
      contextualExecutionIds,
    ],
  );
  const localCursors = useMemo(() => localRenderedCursorRuns(local), [local]);
  useEffect(attachRunSurfaceRegistry, []);
  useEffect(
    () => publishLocalRunSurfaces(localConversations, localCursors),
    [localConversations, localCursors],
  );
  return useMemo(
    () => ({
      ...local,
      renderedCursors: unionRunSurfaces([], localCursors, reports).cursors,
    }),
    [local, localCursors, reports],
  );
}

function cursorConversationId(cursor: AgentCursor, ctx: RunSurfaceContext) {
  return resolveCursorConversationId(cursor, ctx.executions, ctx.agentSessions);
}

function runCursors(run: RunIdentity, ctx: RunSurfaceContext): AgentCursor[] {
  return Array.from(ctx.agentCursors.values()).filter((cursor) =>
    sameRun(run, cursorRunIdentity(cursor, cursorConversationId(cursor, ctx))),
  );
}

/** Match the cursor this webview actually selects on its own display. */
function compactShowsCursor(
  cursor: AgentCursor,
  ctx: RunSurfaceContext,
): boolean {
  const state = getCompactCursorState(ctx.agentCursors, {
    pointingOnly: ctx.dockEnabled,
    projectId: ctx.projectId,
    displayId: ctx.displayId,
    activeDisplayId: ctx.activeDisplayId,
    visibleConversations: ctx.visibleConversations,
    activeGuideId: ctx.activeGuideId,
    contextualExecutionIds: ctx.contextualExecutionIds,
    conversationIdForCursor: (current) => cursorConversationId(current, ctx),
  });
  // Several cursors collapse into a count badge; a pointing one still shows.
  return state.cursor === cursor && (!state.collapsed || !!cursor.pointTarget);
}

/** The workspace views paint every rendered cursor; peek paints only on the
 *  display the user is on. */
function canvasShowsCursor(
  cursor: AgentCursor,
  ctx: RunSurfaceContext,
): boolean {
  if (
    ctx.mode === "peek" &&
    ctx.activeDisplayId !== null &&
    ctx.activeDisplayId !== ctx.displayId
  ) {
    return false;
  }
  return (
    canvasCursorVisibility(cursor, ctx).visible &&
    canvasCursorPaintsOnDisplay(cursor, ctx.displayId, ctx.activeDisplayId)
  );
}

/**
 * Whether this run has its own agent cursor on screen, by the same selection
 * the cursor surfaces render with. A question counts only while that cursor
 * carries the same question. The contextual onboarding guide presents its
 * executions itself.
 */
export function runHasOwnCursor(
  run: RunIdentity,
  ctx: RunSurfaceContext,
  questionId?: string | null,
): boolean {
  for (const cursor of runCursors(run, ctx)) {
    if (questionId && cursor.question?.awaitingInputId !== questionId) continue;
    if (
      cursor.executionId &&
      ctx.contextualExecutionIds.includes(cursor.executionId)
    )
      return (
        ctx.displayId === null ||
        ctx.activeDisplayId === null ||
        ctx.displayId === ctx.activeDisplayId
      );
    if (
      ctx.mode === "compact"
        ? compactShowsCursor(cursor, ctx)
        : canvasShowsCursor(cursor, ctx)
    ) {
      return true;
    }
  }
  return false;
}

function localRenderedCursorRuns(ctx: RunSurfaceContext): RenderedCursorRun[] {
  return [...ctx.agentCursors.values()].flatMap((cursor) => {
    const run = cursorRunIdentity(cursor, cursorConversationId(cursor, ctx));
    return runHasOwnCursor(run, ctx, cursor.question?.awaitingInputId)
      ? [
          {
            ...run,
            ...(cursor.question
              ? { awaitingInputId: cursor.question.awaitingInputId }
              : {}),
          },
        ]
      : [];
  });
}

/** The one surface that shows this run in this window. */
export function runSurface(
  run: RunIdentity,
  ctx: RunSurfaceContext,
  questionId?: string | null,
): RunSurface {
  const desktopApproval =
    !!questionId &&
    runCursors(run, ctx).some(
      (cursor) =>
        cursor.question?.awaitingInputId === questionId &&
        asksDesktopApproval(cursor),
    );
  return runSurfaceOwner(run, {
    visibleConversations: ctx.visibleConversations,
    dockEnabled: ctx.dockEnabled,
    hasOwnCursor:
      (desktopApproval || !ctx.dockEnabled) &&
      (runHasOwnCursor(run, ctx, questionId) ||
        !!ctx.renderedCursors?.some(
          (cursor) =>
            sameRun(run, cursor) &&
            (!questionId || cursor.awaitingInputId === questionId),
        )),
    desktopApproval,
  });
}
